if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity__input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.variantSelects = this.querySelector('variant-radios')
      this.submitButton = this.querySelector('[type="submit"]');
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;
    optionValueUnsubscriber = undefined;

    connectedCallback() {
      // Reacts to a shopper picking a variant option (e.g. a colour swatch).
      // Independent of the quantity-rules logic below, which bails out early
      // when there's no quantity form - variant selection still needs to work
      // on sections that don't render one.
      this.optionValueUnsubscriber = subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, (event) => {
        this.handleOptionValueChange(event);
      });

      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;
      this.setQuantityBoundries();
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
      if (this.optionValueUnsubscriber) {
        this.optionValueUnsubscriber();
      }
    }

    getSelectedOptionValues(variantSelects) {
      // variant-selects' own getAllSelectedOptions() looks for a <fieldset>
      // wrapper per option group, but this theme's swatch/pill/dropdown
      // markup wraps each group in a .product-form__input div instead - so
      // that helper always returns an empty array here. Read the groups
      // this theme actually renders, in DOM (option position) order.
      const values = [];
      variantSelects.querySelectorAll('.product-form__input').forEach((group) => {
        const checkedRadio = group.querySelector('input[type="radio"]:checked');
        const select = group.querySelector('select');
        if (checkedRadio) {
          values.push(checkedRadio.value);
        } else if (select) {
          values.push(select.value);
        }
      });
      return values;
    }

    handleOptionValueChange({ data: { target } }) {
      const variantSelects = this.querySelector('variant-selects');
      if (!variantSelects || !variantSelects.contains(target)) return;

      const dataScript = variantSelects.querySelector('script[type="application/json"]');
      if (!dataScript) return;

      let variants;
      try {
        variants = JSON.parse(dataScript.textContent);
      } catch (error) {
        return;
      }

      const selectedValues = this.getSelectedOptionValues(variantSelects);
      const matchedVariant = variants.find(
        (variant) =>
          Array.isArray(variant.options) &&
          variant.options.length === selectedValues.length &&
          variant.options.every((value, index) => value === selectedValues[index])
      );

      const submitButton = this.querySelector('.atc-button');
      const paymentButtonWrapper = this.querySelector('.payment-button-wrapper');

      if (!matchedVariant) {
        this.setButtonState(submitButton, {
          disabled: true,
          attribute: 'data-requires-selection',
          label: submitButton?.dataset.chooseOptionsLabel,
        });
        if (paymentButtonWrapper) paymentButtonWrapper.style.display = 'none';
        return;
      }

      // Re-query rather than use the cached this.currentVariant - something
      // else in the page replaces this part of the DOM after initial load,
      // leaving that cached reference pointing at a detached node.
      const hiddenVariantInput = this.querySelector('.product-variant-id');
      if (hiddenVariantInput) hiddenVariantInput.value = matchedVariant.id;

      this.updateMedia(matchedVariant);
      this.updatePrice(matchedVariant.id);

      if (!matchedVariant.available) {
        this.setButtonState(submitButton, {
          disabled: true,
          attribute: 'data-unavailable',
          label: submitButton?.dataset.soldOutLabel,
        });
        if (paymentButtonWrapper) paymentButtonWrapper.style.display = 'none';
        return;
      }

      this.setButtonState(submitButton, {
        disabled: false,
        attribute: null,
        label: submitButton?.dataset.defaultLabel,
      });
      if (paymentButtonWrapper) paymentButtonWrapper.style.display = '';
    }

    setButtonState(submitButton, { disabled, attribute, label }) {
      if (!submitButton) return;
      submitButton.disabled = disabled;
      submitButton.removeAttribute('data-requires-selection');
      submitButton.removeAttribute('data-unavailable');
      if (attribute) submitButton.setAttribute(attribute, 'true');

      const labelEl = submitButton.querySelector('.main-atc__label__text');
      if (labelEl && label) labelEl.textContent = label;

      const priceEl = submitButton.querySelector('.main-atc-price');
      if (priceEl) priceEl.classList.toggle('hidden', disabled);
    }

    updateMedia(variant) {
      if (!variant.featured_media) return;
      const mediaGallery = this.closest('.product')?.querySelector('media-gallery');
      if (!mediaGallery) return;
      const mediaId = `${this.dataset.section}-${variant.featured_media.id}`;
      if (!mediaGallery.querySelector(`[data-media-id="${mediaId}"]`)) return;
      mediaGallery.setActiveMedia(mediaId, true);
    }

    updatePrice(variantId) {
      const priceContainer = this.querySelector('.product-page-price');
      if (!priceContainer || !priceContainer.id) return;
      fetch(`${this.dataset.url}?variant=${variantId}&section_id=${this.dataset.section}`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const updatedPrice = html.getElementById(priceContainer.id);
          if (updatedPrice) priceContainer.innerHTML = updatedPrice.innerHTML;
        })
        .catch((e) => {
          console.error(e);
        });
    }

    setQuantityBoundries() {
      const data = {
        cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
        min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
        max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
        step: this.input.step ? parseInt(this.input.step) : 1
      }

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.input.min = min;
      this.input.max = max;
      this.input.value = min;
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);  
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      this.querySelector('.quantity__rules-cart .loading-overlay').classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`).then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector('.quantity__rules-cart .loading-overlay').classList.add('hidden');
      });
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) { 
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }
  }
)};



